import debounce from 'lodash/debounce';
import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { PreferencesContextType } from '@/app/providers/preferences-provider/withPreferences';
import type { ThemeName } from '@/app/providers/theme-provider/withThemeName';
import type { ConnectionMethod } from '@/features/editor/local-connect/device-instructions/DeviceInstructionsModal';
import type { Platform, QueryStateParams } from '@/shared/types/tile-packet.types';
import { isMobile } from '@/shared/utils/detect-platform/detect-platform';
import { useSelector } from '@/store/hooks';

export type PanelType = 'errors' | 'logs';
export type EditorModeType = 'normal' | 'vim';

export type PreferencesType = {
  deviceConnectionMethod: ConnectionMethod;
  devicePreviewPlatform: Platform;
  devicePreviewShown: boolean;
  editorMode: EditorModeType;
  fileTreeShown: boolean;
  panelsShown: boolean;
  panelType: PanelType;
  theme: ThemeName;
};

export type SetPreferencesType = (overrides: Partial<PreferencesType>) => void;

type Props = {
  queryParams: QueryStateParams;
  cookies: {
    get: (key: string) => string | undefined;
    set?: (key: string, value: string) => void;
  };
  testPreviewPlatform?: Platform;
  children: ReactNode;
};

const EDITOR_CONFIG_KEY = 'tile-packet-editor-config';

const defaults: PreferencesType = {
  deviceConnectionMethod: 'qr-code',
  devicePreviewPlatform: 'web',
  devicePreviewShown: true,
  editorMode: 'normal',
  fileTreeShown: !isMobile(),
  panelsShown: false,
  panelType: 'errors',
  theme: 'light',
};

export const PreferencesContext = createContext<PreferencesContextType | null>(null);

/**
 * Compute the preferences seed from cookies + localStorage + query
 * params. Mirrors the legacy class constructor's order of precedence
 * (saved cookie wins over query, query wins over test override, test
 * override wins over the static defaults). Done as a `useState`
 * initializer so we only run it once per mount.
 */
function buildInitialPreferences(
  cookies: Props['cookies'],
  queryParams: QueryStateParams,
  testConnectionMethod: ConnectionMethod | undefined,
  testPreviewPlatform: Platform | undefined,
): PreferencesType {
  let overrides: Partial<PreferencesType> = {};

  try {
    if (localStorage) {
      overrides.editorMode = localStorage.getItem('editorMode') as EditorModeType;
      overrides.theme = localStorage.getItem('theme') as ThemeName;
    }
  } catch {
    /* ignore */
  }

  try {
    overrides = JSON.parse(cookies.get(EDITOR_CONFIG_KEY) || '') || {};
    if (overrides.devicePreviewPlatform !== 'mydevice') {
      overrides.devicePreviewPlatform = undefined;
    }
  } catch {
    /* ignore */
  }

  try {
    const { theme, platform } = queryParams;
    if (theme === 'light' || theme === 'dark') overrides.theme = theme;
    if (
      platform === 'android' ||
      platform === 'ios' ||
      platform === 'mydevice' ||
      platform === 'web'
    ) {
      overrides.devicePreviewPlatform = platform;
    }
  } catch {
    /* ignore */
  }

  return {
    ...defaults,
    ...overrides,
    deviceConnectionMethod:
      overrides.deviceConnectionMethod || testConnectionMethod || defaults.deviceConnectionMethod,
    devicePreviewPlatform:
      overrides.devicePreviewPlatform || testPreviewPlatform || defaults.devicePreviewPlatform,
  };
}

export default function PreferencesProvider({
  queryParams,
  cookies,
  testPreviewPlatform,
  children,
}: Props) {
  const testConnectionMethod = useSelector(
    (state: any) =>
      state.splitTestSettings?.defaultConnectionMethod as ConnectionMethod | undefined,
  );

  const [preferences, setPreferencesState] = useState<PreferencesType>(() =>
    buildInitialPreferences(cookies, queryParams, testConnectionMethod, testPreviewPlatform),
  );

  // Debounced persistence — refs so the debounced callback always
  // sees the latest cookies + preferences without re-creating the
  // debounce timer on every change.
  const cookiesRef = useRef(cookies);
  const preferencesRef = useRef(preferences);
  cookiesRef.current = cookies;
  preferencesRef.current = preferences;

  const persistPreferences = useMemo(
    () =>
      debounce(() => {
        try {
          cookiesRef.current.set?.(EDITOR_CONFIG_KEY, JSON.stringify(preferencesRef.current));
        } catch {
          /* ignore */
        }
      }, 1000),
    [],
  );

  // Cancel any in-flight debounce on unmount.
  useEffect(() => () => persistPreferences.cancel?.(), [persistPreferences]);

  const setPreferences = useCallback(
    (overrides: Partial<PreferencesType>) => {
      setPreferencesState((prev) => ({ ...prev, ...overrides }));
      persistPreferences();
    },
    [persistPreferences],
  );

  // Memoize the context value so consumers don't re-render whenever
  // PreferencesProvider re-renders for an unrelated reason.
  const ctx = useMemo<PreferencesContextType>(
    () => ({ preferences, setPreferences }),
    [preferences, setPreferences],
  );

  return <PreferencesContext.Provider value={ctx}>{children}</PreferencesContext.Provider>;
}
